import { useState } from 'react';
import {
  MessageCircle,
  Mail,
  Clock,
  Layers,
  Tag,
  CheckSquare,
  Share2,
  ClipboardList,
  Zap,
  CheckCircle,
  XCircle,
  MinusCircle,
  Loader2,
} from 'lucide-react';

/**
 * Rich, backend-driven automation execution timeline. Renders the actual recorded
 * steps (automation_execution_steps) — what ran, when, the result, and a plain
 * English summary per action (message sent, lead created, stage reached, …).
 */

export interface AutomationTimelineStep {
  id: string;
  step_order: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  started_at?: string | null;
  completed_at?: string | null;
  input_data?: any;
  output_data?: any;
  error_message?: string | null;
  automation_node?: {
    node_type?: string;
    node_subtype?: string;
    name?: string;
    configuration?: any;
  } | null;
}

interface Props {
  steps: AutomationTimelineStep[];
  /** The execution row (for the trigger-subject banner). Optional. */
  execution?: any;
}

const NODE_META: Record<string, { label: string; Icon: typeof Mail; tint: string }> = {
  sms: { label: 'Send SMS', Icon: MessageCircle, tint: 'text-blue-600' },
  email: { label: 'Send Email', Icon: Mail, tint: 'text-indigo-600' },
  delay: { label: 'Wait', Icon: Clock, tint: 'text-orange-600' },
  create_crm_lead: { label: 'Create CRM Lead', Icon: Layers, tint: 'text-purple-600' },
  assign_tags: { label: 'Assign Tags', Icon: Tag, tint: 'text-emerald-600' },
  check: { label: 'Condition', Icon: CheckSquare, tint: 'text-amber-600' },
  create_task: { label: 'Create Task', Icon: CheckSquare, tint: 'text-sky-600' },
  add_to_automation: { label: 'Add to Automation', Icon: Share2, tint: 'text-fuchsia-600' },
  send_live_intake_form: { label: 'Send Intake Form', Icon: ClipboardList, tint: 'text-teal-600' },
};

function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtDuration(startIso?: string | null, endIso?: string | null): string {
  if (!startIso || !endIso) return '';
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return '<1s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/** Plain-English summary of what happened at a step, derived from output_data. */
function describeStep(step: AutomationTimelineStep): string[] {
  const subtype = step.automation_node?.node_subtype || '';
  const o = step.output_data || {};
  const lines: string[] = [];

  if (step.status === 'failed') {
    lines.push(step.error_message || (o.error ? String(o.error) : 'Action failed'));
    return lines;
  }

  switch (subtype) {
    case 'sms': {
      if (o.action === 'sms_skipped') {
        lines.push(`Skipped — ${String(o.skipped_reason || 'no valid recipients').replace(/_/g, ' ')}`);
        break;
      }
      const to = Array.isArray(o.recipients) ? o.recipients.filter(Boolean).join(', ') : '';
      if (to) lines.push(`To: ${to}`);
      if (o.message) lines.push(`“${String(o.message).trim()}”`);
      if (typeof o.success_count === 'number') {
        lines.push(`${o.success_count} sent${o.failed_count ? `, ${o.failed_count} failed` : ''}`);
      }
      break;
    }
    case 'email': {
      if (o.action === 'email_skipped' || o.skipped_reason) {
        lines.push(`Skipped — ${String(o.skipped_reason || 'no recipient').replace(/_/g, ' ')}`);
        break;
      }
      const to = o.recipient || o.to || (Array.isArray(o.recipients) ? o.recipients.join(', ') : '');
      if (to) lines.push(`To: ${to}`);
      if (o.subject) lines.push(`Subject: ${o.subject}`);
      if (o.method) lines.push(`Sent via ${o.method}`);
      break;
    }
    case 'delay': {
      if (o.reminder_scheduled_for) {
        lines.push(`Waited until ${fmtDateTime(o.reminder_scheduled_for)}`);
        if (o.appointment_time) lines.push(`Appointment: ${fmtDateTime(o.appointment_time)}`);
      } else if (o.duration_minutes) {
        lines.push(`Waited ${o.duration_minutes} min`);
      } else if (o.message) {
        lines.push(String(o.message));
      }
      break;
    }
    case 'create_crm_lead': {
      const pipeline = o.pipeline_name;
      const stage = o.stage_name;
      if (pipeline || stage) {
        lines.push(`Created lead in ${pipeline || 'pipeline'}${stage ? ` → ${stage}` : ''}`);
      } else {
        lines.push('Created CRM lead');
      }
      if (o.status) lines.push(`Status: ${o.status}`);
      break;
    }
    case 'assign_tags': {
      const tags = o.tags || o.assigned_tags || o.tag_names;
      if (Array.isArray(tags) && tags.length) lines.push(`Tagged: ${tags.join(', ')}`);
      else lines.push('Tags assigned');
      break;
    }
    case 'check': {
      const matched = o.matched ?? (o.shouldStop === false);
      lines.push(o.shouldStop ? 'Condition not met — automation stopped' : 'Condition met — continued');
      if (o.branch || o.ai_routing_branch) lines.push(`Branch: ${o.branch || o.ai_routing_branch}`);
      void matched;
      break;
    }
    case 'create_task': {
      lines.push(o.title ? `Task created: ${o.title}` : 'Task created');
      break;
    }
    case 'add_to_automation': {
      lines.push(o.target_automation_name ? `Enrolled in “${o.target_automation_name}”` : 'Added to another automation');
      break;
    }
    case 'send_live_intake_form': {
      lines.push(o.form_title ? `Sent intake form: ${o.form_title}` : 'Sent live intake form');
      break;
    }
    default: {
      if (o.message) lines.push(String(o.message));
    }
  }

  return lines;
}

function statusVisual(status: AutomationTimelineStep['status']) {
  switch (status) {
    case 'completed':
      return { Icon: CheckCircle, ring: 'border-green-300 bg-green-100 text-green-700', card: 'border-green-200 bg-green-50', label: 'Completed', badge: 'bg-green-100 text-green-800' };
    case 'failed':
      return { Icon: XCircle, ring: 'border-red-300 bg-red-100 text-red-700', card: 'border-red-200 bg-red-50', label: 'Failed', badge: 'bg-red-100 text-red-800' };
    case 'running':
      return { Icon: Loader2, ring: 'border-blue-300 bg-blue-100 text-blue-700', card: 'border-blue-200 bg-blue-50', label: 'Running', badge: 'bg-blue-100 text-blue-800' };
    case 'skipped':
      return { Icon: MinusCircle, ring: 'border-gray-300 bg-gray-100 text-gray-500', card: 'border-gray-200 bg-gray-50', label: 'Skipped', badge: 'bg-gray-100 text-gray-600' };
    default:
      return { Icon: Clock, ring: 'border-gray-300 bg-gray-100 text-gray-500', card: 'border-gray-200 bg-gray-50', label: 'Pending', badge: 'bg-gray-100 text-gray-600' };
  }
}

/** Best-effort "who/what triggered this run" banner from the execution trigger_data. */
function TriggerSubject({ execution }: { execution: any }) {
  const td = execution?.trigger_data || {};
  const b = td.booking_data;
  let subjectLabel = '';
  let context = '';
  const eventType = td.event_type || td.trigger_type;

  if (b) {
    const name = [b.customer?.first_name, b.customer?.last_name].filter(Boolean).join(' ').trim();
    subjectLabel = name || b.customer?.phone || b.customer?.email || 'Customer';
    context = b.booking_number ? `Booking #${b.booking_number}` : 'Booking';
  } else if (td.opportunity_data || td.crm_data) {
    const crm = td.opportunity_data || td.crm_data;
    const name = [crm.first_name, crm.last_name].filter(Boolean).join(' ').trim();
    subjectLabel = name || crm.name || 'Lead';
    context = [crm.pipeline_name, crm.stage_name].filter(Boolean).join(' → ') || 'CRM';
  } else if (td.form_data) {
    const f = td.form_data;
    subjectLabel = f.name || [f.first_name, f.last_name].filter(Boolean).join(' ').trim() || f.email || 'Form submission';
    context = 'Form submission';
  } else {
    subjectLabel = eventType ? String(eventType).replace(/_/g, ' ') : 'Automation run';
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 mb-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
        <Zap className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-gray-900 truncate">{subjectLabel}</div>
        <div className="text-[12px] text-gray-500 truncate">
          {[context, eventType ? `${String(eventType).replace(/_/g, ' ')} trigger` : null].filter(Boolean).join(' · ')}
        </div>
      </div>
    </div>
  );
}

export default function AutomationStepTimeline({ steps, execution }: Props) {
  const [rawOpenId, setRawOpenId] = useState<string | null>(null);
  const ordered = [...(steps || [])].sort((a, b) => (a.step_order || 0) - (b.step_order || 0));

  return (
    <div>
      {execution && <TriggerSubject execution={execution} />}

      <h3 className="font-medium text-gray-900 mb-4">Execution Steps</h3>

      {ordered.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Clock className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No steps have run yet for this execution.</p>
        </div>
      ) : (
        <div className="relative space-y-3">
          {ordered.map((step, index) => {
            const subtype = step.automation_node?.node_subtype || '';
            const meta = NODE_META[subtype] || { label: step.automation_node?.name || subtype || 'Action', Icon: Zap, tint: 'text-gray-600' };
            const vis = statusVisual(step.status);
            const StatusIcon = vis.Icon;
            const NodeIcon = meta.Icon;
            const summary = describeStep(step);
            const duration = fmtDuration(step.started_at, step.completed_at);
            const rawOpen = rawOpenId === step.id;

            return (
              <div key={step.id} className="relative">
                {index < ordered.length - 1 && (
                  <div className="absolute left-[19px] top-11 h-[calc(100%-12px)] w-0.5 bg-gray-200" />
                )}
                <div className={`relative flex items-start gap-3 rounded-lg border p-3 ${vis.card}`}>
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 ${vis.ring}`}>
                    <NodeIcon className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[14px] font-semibold ${meta.tint}`}>{meta.label}</span>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${vis.badge}`}>
                            <StatusIcon className={`h-3 w-3 ${step.status === 'running' ? 'animate-spin' : ''}`} />
                            {vis.label}
                          </span>
                        </div>
                        {step.automation_node?.name && step.automation_node.name !== meta.label && (
                          <div className="text-[12px] text-gray-500 truncate">{step.automation_node.name}</div>
                        )}
                      </div>
                      <span className="shrink-0 text-[11px] text-gray-400">#{step.step_order}</span>
                    </div>

                    {summary.length > 0 && (
                      <div className="mt-1.5 space-y-0.5">
                        {summary.map((line, i) => (
                          <p
                            key={i}
                            className={`text-[13px] ${step.status === 'failed' ? 'text-red-700' : 'text-gray-700'} ${i === 0 && line.startsWith('“') ? 'italic' : ''} break-words`}
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    )}

                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-400">
                      {step.completed_at ? (
                        <span>{fmtDateTime(step.completed_at)}</span>
                      ) : step.started_at ? (
                        <span>Started {fmtDateTime(step.started_at)}</span>
                      ) : null}
                      {duration && <span>· {duration}</span>}
                      {(step.output_data && Object.keys(step.output_data).length > 0) || step.input_data ? (
                        <button
                          type="button"
                          onClick={() => setRawOpenId(rawOpen ? null : step.id)}
                          className="text-blue-500 hover:text-blue-600 font-medium"
                        >
                          {rawOpen ? 'Hide details' : 'View details'}
                        </button>
                      ) : null}
                    </div>

                    {rawOpen && (
                      <div className="mt-2 space-y-2">
                        {step.output_data && Object.keys(step.output_data).length > 0 && (
                          <div>
                            <div className="text-[11px] font-medium text-gray-500 mb-1">Output</div>
                            <pre className="max-h-48 overflow-auto rounded bg-white border border-gray-200 p-2 text-[11px] text-gray-700">
                              {JSON.stringify(step.output_data, null, 2)}
                            </pre>
                          </div>
                        )}
                        {step.input_data && Object.keys(step.input_data).length > 0 && (
                          <div>
                            <div className="text-[11px] font-medium text-gray-500 mb-1">Input</div>
                            <pre className="max-h-48 overflow-auto rounded bg-white border border-gray-200 p-2 text-[11px] text-gray-700">
                              {JSON.stringify(step.input_data, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
